/**
 * The body switch: what a change of focused body rebuilds, the `?body=` and
 * `?site=` refusals, and the URL's site resolved against the body that owns it.
 */

import type * as THREE from 'three';

import type { AtlasLive, AtlasState } from '../atlas';
import { escapeHtml } from '../html.ts';
import type { BodyRow } from '../world/types';
import type { AtmosphereViewHandle } from './atmosphereView.ts';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import type { CameraRigHandle } from './cameraRig.ts';
import type { DescentAnchorHandle } from './descentAnchor.ts';
import type { DescentLoadHandle } from './descentLoad.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { GlobeMaterialHandle } from './globeMaterial.ts';
import type { HexViewHandle } from './hexView.ts';
import type { InjectsHandle } from './injects.ts';
import type { InspectorHandle } from './inspector.ts';
import type { OverlayViewHandle } from './overlayView.ts';
import type { SiteChipsHandle } from './panels/siteChips.ts';
import type { PlotBuildHandle } from './plotBuild.ts';
import type { PyramidViewHandle } from './pyramidView.ts';
import type { ShaderWarmHandle } from './shaderWarm.ts';
import type { Site, SiteRegistryState } from './siteRegistry.ts';

export interface BodySwitchDeps {
  readonly state: AtlasState;
  urlSite(): string | null;
  selectSiteBody(id: string | null): void;
  readonly bodyReaders: BodyReadersHandle;
  readonly bodyScene: BodySceneHandle;
  readonly pyramidView: PyramidViewHandle;
  readonly globeMaterial: GlobeMaterialHandle;
  readonly focusRig: FocusRigHandle;
  readonly descentLoad: DescentLoadHandle;
  readonly descentAnchorHandle: DescentAnchorHandle;
  readonly plotBuild: PlotBuildHandle;
  readonly atmosphereView: AtmosphereViewHandle;
  readonly shaderWarm: ShaderWarmHandle;
  readonly hexViewSeam: HexViewHandle;
  readonly injects: InjectsHandle;
  readonly siteRegistry: SiteRegistryState;
  readonly live: AtlasLive;
  /** Built after this module: each arrives as a getter. */
  readonly cameraRig: CameraRigHandle;
  readonly overlayView: OverlayViewHandle;
  readonly siteChips: SiteChipsHandle;
  readonly inspector: InspectorHandle;
}

/** The switch's own lets that the mount, the tick and the probe read or write. */
export interface BodySwitchState {
  readonly bodyRefused: string | null;
  readonly siteRefused: string | null;
  urlSiteResolved: boolean;
  appliedBodyId: string | null;
}

export interface BodySwitchHandle {
  /** The slug `?site=` named, or null. */
  readonly urlSiteId: string | null;
  syncBody(): void;
  refuseBody(id: string, rows: readonly BodyRow[]): void;
  refuseSite(slug: string): void;
  applyUrlSite(): void;
  reportNoPyramid(row: BodyRow): void;
  rebuildDescentTarget(): void;
  openSiteAt(ground: { lat: number; lon: number } | null): void;
  /** Drop a patch built from the leaves once the open descent has moved off it. */
  releasePatchOffDescent(): void;
  readonly state: BodySwitchState;
}

export function createBodySwitch(deps: BodySwitchDeps): BodySwitchHandle {
  const { state, urlSite, selectSiteBody, bodyScene, pyramidView, globeMaterial, shaderWarm, descentLoad } = deps;
  const { arcDeg, plotReleaseOnLand, plotKeepBody } = descentLoad;
  const { ladderBody, requestSets, registryDatumM } = deps.bodyReaders;
  const { focusCam, fitOpening } = deps.focusRig;
  const { anchorLatLon } = deps.descentAnchorHandle;
  const { releasePlot } = deps.plotBuild;
  const { syncAtmoMount } = deps.atmosphereView;
  const { buildQuadLines, syncHexOverlay, applyQuadAvailability } = deps.hexViewSeam;

  /** Set when `?body=` named an id the registry does not have; nothing opens. */
  let bodyRefused: string | null = null;
  /** The slug `?site=` named, and whether it has been resolved against a body yet. */
  const urlSiteId = urlSite();
  let siteRefused: string | null = null;
  let urlSiteResolved = false;
  /** The body `syncBody` last built for, so a body change is built once. */
  let appliedBodyId: string | null = null;

  /** Is the open descent's point inside the loaded patch's inscribed radius of
   *  the point it was built for; not its centre, which a siteless aim moves. */
  function descentOnPlot(): boolean {
    const f = deps.live.plotField;
    const perDeg = f?.meta.frame?.m_per_deg_lat;
    const { descentFor, pyramidPlotFor } = descentLoad.state;
    if (!f || perDeg === undefined || descentFor === null || pyramidPlotFor === null) return false;
    return arcDeg(descentFor.lat, descentFor.lon, pyramidPlotFor.lat, pyramidPlotFor.lon) * perDeg
      <= f.meta.hex.across_flats_m / 2;
  }

  /**
   * Everything below the orrery that is a function of WHICH body: its orrery
   * node, its site list, its quadrangle outlines and the hex ring on its
   * ground. Called on the body change and when the registry lands, and it does
   * not re-aim the camera - `rebuildDescentTarget` is what does that.
   */
  function syncBody(): void {
    const wasBody = appliedBodyId;
    appliedBodyId = ladderBody();
    requestSets(appliedBodyId);
    if (appliedBodyId !== wasBody) bodyScene.state.shapeField = null;
    const wasGlobe = bodyScene.state.bodyNode?.mesh ?? null;
    const wasCoverage = bodyScene.state.bodyNode?.coverage ?? null;
    bodyScene.state.bodyNode = bodyScene.state.orrery?.byId(appliedBodyId) ?? null;
    if (!deps.injects.state.shapeSphereDebug) bodyScene.state.shapeGlobes?.request(appliedBodyId);
    // The underlay and the globe chunk are the FOCUSED globe's, and both used
    // to stay on the body the focus left: Mars kept the pyramid's draw order
    // and its missing depth for the rest of the session, which is how it came
    // to be drawn in front of Phobos.
    if (wasGlobe && wasGlobe !== bodyScene.state.bodyNode?.mesh) {
      if (pyramidView.underlayMesh === wasGlobe) {
        pyramidView.restoreUnderlay(wasGlobe);
        pyramidView.globeUnderlay = false;
        pyramidView.underlayMesh = null;
        pyramidView.globeDepthPushM.value = 0;
      }
      globeMaterial.clearGlobeShader(wasGlobe.material as THREE.MeshStandardMaterial, wasCoverage);
    }
    // `goTo`'s own point, read before its reframe re-aims: a LAND's sub-ship point.
    const { bodyId: forBody, latDeg, lonDeg } = focusCam.state;
    descentLoad.state.descentFor = latDeg === null || lonDeg === null ? null : { body: forBody, lat: latDeg, lon: lonDeg };
    // One body's ground, and `ensurePlotMesh` returns early while a mesh is up:
    // without this the last body's lattice stays in `dtmGroup` under the new
    // body's card, its sun and its numbers, and the rover drives on it.
    // A patch from this body's own leaves is kept only for a descent onto its ground:
    // `appliedBodyId` also follows cruise's pull, and the ship may have moved off it.
    if (plotReleaseOnLand || descentLoad.state.pyramidPlotBody !== appliedBodyId || anchorLatLon() === null
      || !(plotKeepBody || descentOnPlot())) releasePlot();
    // Ahead of the globe's own hook: `syncAtmoMount` is what moves
    // `globeAtmoRef` off the body the focus left, and a hook installed before
    // it binds this globe's program to the departed body's air - which on a
    // body with no air of its own nothing later takes back.
    if (!globeMaterial.switchDebug.legacyGlobeProgram) syncAtmoMount();
    if (bodyScene.state.bodyNode) {
      globeMaterial.installGlobeShader(bodyScene.state.bodyNode.mesh.material as THREE.MeshStandardMaterial, bodyScene.state.bodyNode.coverage);
    }
    if (globeMaterial.switchDebug.legacyGlobeProgram) syncAtmoMount();
    // A body with no air of its own never reaches `mountAtmo`, and its tiles
    // and plot still need their programs before the ladder does.
    shaderWarm.warmDue = true;
    shaderWarm.warmQuiet = 0;
    // A filter typed for one body's names means nothing on the next body's.
    deps.siteChips.resetFilter();
    selectSiteBody(ladderBody());
    applyUrlSite();
    deps.siteChips.renderSiteChips();
    buildQuadLines();
    syncHexOverlay();
    applyQuadAvailability();
    deps.live.needsRender = true;
  }

  /**
   * An unknown `?body=` is refused by name.
   *
   * The registry is the list of bodies there are, so an id that is not in it
   * cannot be opened, and opening the default in its place would be a silent
   * lie about which ground is on screen. Nothing is built and nothing is
   * fetched: the card and the console name the id and the run stops here.
   */
  function refuseBody(id: string, rows: readonly BodyRow[]): void {
    bodyRefused = id;
    state.bodyId = null;
    state.tier = 0;
    console.error(`[atlas] ?body=${id} is not a body in the registry; the Atlas did not `
      + `open. Known bodies: ${rows.map((r) => r.id).join(', ')}`);
    const known = escapeHtml(rows.map((r) => r.id).join(', '));
    deps.overlayView.state.notInSlice = `
      <div class="atlas-card warn">
        <div class="atlas-card-title">UNKNOWN BODY</div>
        <div class="atlas-card-row">?body=${escapeHtml(id)} is not in the body registry,
          so the Atlas did not open.</div>
        <div class="atlas-card-hint">Known bodies: ${known}.</div>
      </div>
    `;
    deps.overlayView.renderOverlay();
  }

  /**
   * An unknown `?site=` is refused by name.
   *
   * The body opened, so the refusal is narrower than `refuseBody`'s: the
   * ladder is what does not aim, because a slug this body has no row for
   * names ground that is not there. Descending to its first site instead
   * would put different ground on screen than the link asked for.
   */
  function refuseSite(slug: string): void {
    siteRefused = slug;
    state.tier = 0;
    // The refusal lands after the URL's own rung has already framed, so
    // without this `?site=nope&atlasTier=5` says SYSTEM over a 16.9 km view.
    fitOpening(deps.cameraRig.aspect());
    deps.live.cameraDirty = true;
    const known = deps.siteRegistry.bodySites.map((st) => st.id);
    console.error(`[atlas] ?site=${slug} is not a site on ${deps.siteRegistry.siteBodyId ?? 'this body'}; the `
      + `descent was not aimed. Known sites: ${known.length ? known.join(', ') : 'none'}`);
    deps.overlayView.state.notInSlice = `
      <div class="atlas-card warn">
        <div class="atlas-card-title">UNKNOWN SITE</div>
        <div class="atlas-card-row">?site=${escapeHtml(slug)} is not a site on
          ${escapeHtml(deps.siteRegistry.siteBodyId ?? 'this body')}, so the descent was not aimed.</div>
        <div class="atlas-card-hint">Known sites: ${escapeHtml(known.length ? known.join(', ') : 'none')}.</div>
      </div>
    `;
    deps.overlayView.renderOverlay();
  }

  /**
   * Honour `?site=` once, against the body that owns it.
   *
   * Called wherever the body's own row list is re-derived, and it resolves at
   * the first call that has rows to resolve against: before the registry
   * lands there is nothing to name, and after a chip click or a body change
   * the URL has had its say.
   */
  function applyUrlSite(): void {
    if (urlSiteId === null || urlSiteResolved || deps.siteRegistry.siteBodyId === null || deps.siteRegistry.SITES.length === 0) return;
    urlSiteResolved = true;
    const i = deps.siteRegistry.bodySites.findIndex((st) => st.id === urlSiteId);
    if (i < 0) { refuseSite(urlSiteId); return; }
    deps.siteRegistry.siteIdx = i;
  }

  /**
   * A body the URL opened that has no pyramid: say so by name.
   *
   * The orrery still frames it - every body is orbitable - and the descent
   * stops where its whole disc is in frame. Nothing under `/dtm/` is asked
   * for, so the alternative to this line is silence.
   */
  function reportNoPyramid(row: BodyRow): void {
    console.warn(`[atlas] ${row.id} has no baked pyramid: ${row.availability.reason} `
      + (row.corridor !== null
        ? 'The orrery globe is the cloud deck.'
        : 'The orrery globe is the surface.'));
    deps.inspector.flashInspector(`${row.display_name} is catalogued as a ${row.class.replace(/^\d_/, '')}: `
      + `${row.availability.reason}`);
  }

  /** How far from its centre a grid of `plots` cells can reach, one cell a step. */
  function surveyReachM(st: Site): number {
    const r = (st.bake?.radius_km ?? Math.sqrt(st.plot_area_km2 / (1.5 * Math.sqrt(3)))) * 1000;
    return r * (1 + Math.sqrt(3) * Math.max(0, st.plots - 1));
  }

  /** Open the site whose survey can hold this ground, or none (null reopens the first): a jump left on
   *  the open site built that site's claim and stood the walkaround on its centre. */
  function openSiteAt(ground: { lat: number; lon: number } | null): void {
    if (!deps.siteRegistry.sitesLoaded) return;
    selectSiteBody(ladderBody());
    const reg = deps.siteRegistry;
    if (reg.bodySites.length === 0) return;
    let next = ground === null ? Math.max(0, reg.siteIdx) : -1;
    if (ground !== null) {
      const radiusM = registryDatumM(ladderBody());
      if (radiusM <= 0) return;
      let bestM = Infinity;
      reg.bodySites.forEach((st, i) => {
        const m = (arcDeg(ground.lat, ground.lon, st.center_lat_deg, st.center_lon_deg) * Math.PI / 180) * radiusM;
        if (m <= surveyReachM(st) && m < bestM) { bestM = m; next = i; }
      });
    }
    if (next === reg.siteIdx) return;
    reg.siteIdx = next;
    urlSiteResolved = true;
    // The loaded field is the other site's, and would otherwise stand in as this descent's anchor.
    releasePlot();
    deps.siteChips.renderSiteChips();
    rebuildDescentTarget();
  }

  /** A jump on the body the ladder is already on skips `syncBody`'s release: a patch left from the last
   *  landing would stand the walkaround there instead of on the new ground. */
  function releasePatchOffDescent(): void {
    if (deps.injects.state.JUMP_KEEPS_PATCH || plotKeepBody) return;
    if (descentLoad.state.pyramidPlotBody === appliedBodyId && !descentOnPlot()) releasePlot();
  }

  /** Re-aim the whole ladder after the site registry lands or the site changes. */
  function rebuildDescentTarget(): void {
    if (deps.live.disposed) return;
    syncHexOverlay();
    applyQuadAvailability();
    // The framing below ORBIT is aimed at the site's own ground, which is an
    // answer only the site registry has, so a mount that opened deep re-aims
    // once it lands - and only if the player has not aimed it themselves.
    if (state.tier >= 1 && focusCam.state.latDeg === null) deps.cameraRig.applyTierFraming(state.tier);
    deps.live.needsRender = true;
  }

  return {
    urlSiteId, syncBody, refuseBody, refuseSite, applyUrlSite, reportNoPyramid, rebuildDescentTarget, openSiteAt,
    releasePatchOffDescent,
    state: {
      get bodyRefused() { return bodyRefused; },
      get siteRefused() { return siteRefused; },
      get urlSiteResolved() { return urlSiteResolved; },
      set urlSiteResolved(v) { urlSiteResolved = v; },
      get appliedBodyId() { return appliedBodyId; },
      set appliedBodyId(v) { appliedBodyId = v; },
    },
  };
}
